import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ActionStrip from './ActionStrip.js';
import AttentionStrip from './AttentionStrip.js';
import BuildQueueCard from './BuildQueueCard.js';
import CancelledCard from './CancelledCard.js';
import OpenPrsCard from './OpenPrsCard.js';
import RequestsCard from './RequestsCard.js';
import ThemedShell from './ThemedShell.js';
import TrackDrawer from './TrackDrawer.js';
import { isActionable } from './statusMap.js';
import { useTracks } from './useTracks.js';
import type { TrackState } from '../../api.js';

const ATTENTION_STATES: TrackState[] = ['BLOCKED_HUMAN', 'AWAITING_PREREQS'];
const DONE_STATES: TrackState[] = ['DONE', 'MERGED'];
const MAX_RECENT_COMPLETED = 10;

function sortBucket(state: TrackState): number {
  if (state === 'BUILDING') return 0;
  if (state === 'QUEUED') return 1;
  if (ATTENTION_STATES.includes(state)) return 2;
  return 3;
}

const MAX_BUILD_ROWS = 15;

export default function ConductorDashboard() {
  const { tracks, loading, error, refresh } = useTracks();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openTrackId, setOpenTrackId] = useState<string | null>(() => searchParams.get('track'));

  function openTrack(id: string) {
    setOpenTrackId(id);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('track', id);
      return next;
    });
  }

  function closeTrack() {
    setOpenTrackId(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('track');
      return next;
    });
  }

  const numRunning = tracks.filter((t) => t.state === 'BUILDING').length;
  const numQueued = tracks.filter((t) => t.state === 'QUEUED').length;
  const numPassed = tracks.filter((t) => t.state === 'DONE' || t.state === 'MERGED').length;

  const actionableTracks = tracks.filter((t) => isActionable(t.state));

  const sortedTracks = tracks
    .filter((t) => !isActionable(t.state))
    .sort((a, b) => {
      const bucketDiff = sortBucket(a.state) - sortBucket(b.state);
      if (bucketDiff !== 0) return bucketDiff;
      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    })
    .slice(0, MAX_BUILD_ROWS);

  const recentCompleted = tracks
    .filter((t) => DONE_STATES.includes(t.state))
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
    .slice(0, MAX_RECENT_COMPLETED);

  const toolbarExtra = (
    <>
      <span className="conductor-live-stats">
        {numRunning} running · {numQueued} queued · {numPassed} passed
      </span>
      <Link to="/drop-idea" className="btn-conductor" style={{ textDecoration: 'none' }}>
        New request
      </Link>
    </>
  );

  return (
    <ThemedShell activeNav="dashboard" toolbarExtra={toolbarExtra}>
      {(appearance) => (
        <>
          {loading && <div className="empty">Loading…</div>}
          {error && (
            <div
              style={{
                background: 'var(--bad-bg)',
                color: 'var(--bad)',
                padding: '14px 16px',
                borderRadius: 'var(--radius-sm)',
                marginBottom: 16,
              }}
            >
              {error}
            </div>
          )}

          {!loading && !error && (
            <>
              <AttentionStrip tracks={tracks} onOpen={openTrack} />
              <ActionStrip
                tracks={actionableTracks}
                recentCompleted={recentCompleted}
                onOpen={openTrack}
                refreshList={refresh}
              />
              <div className="conductor-grid">
                <BuildQueueCard
                  tracks={sortedTracks}
                  direction={appearance.direction}
                  recentCompleted={recentCompleted}
                  onOpen={openTrack}
                />
                <RequestsCard tracks={tracks} appearance={appearance} onOpen={openTrack} />
              </div>
              <OpenPrsCard />
              <CancelledCard tracks={tracks} />
            </>
          )}

          <TrackDrawer trackId={openTrackId} tracks={tracks} onClose={closeTrack} refreshList={refresh} />
        </>
      )}
    </ThemedShell>
  );
}
